import React, { useEffect, useRef, useState } from "react";
import axios, { CancelTokenSource } from "axios";
import { Form } from "react-bootstrap";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading, FormCheck } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import handleException from "go-core/api/handleException";
import { ApiError } from "go-core/api/types";
import { ApplicationUpdateApi } from "../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../services/Api/api";
import { AppState } from "../../../utils/types";

interface Props {
	form: UseFormReturn<ApplicationUpdateApi>;
	fetchApplication: () => Promise<void>;
	setMenuErrors: React.Dispatch<React.SetStateAction<ApiError[]>>;
	appState: AppState;
}

const GoOrderSynchronizationForm = ({ form, fetchApplication, setMenuErrors, appState }: Props) => {
	const { t } = useTranslation();
	const {
		register,
		formState: { errors },
		setError,
	} = form;
	const [menuSending, setMenuSending] = useState(false);
	const menuSendingIntervalRef = useRef<number | undefined>();
	const cancelTokenSource = useRef<CancelTokenSource>();
	const { addSuccessFlash, addFlash } = useFlash();

	useEffect(() => {
		return () => {
			if (cancelTokenSource.current !== undefined) {
				cancelTokenSource.current.cancel();
			}
			window.clearTimeout(menuSendingIntervalRef.current);
		};
	}, []);

	const handleSendingMenuTimeout = (jobId: string) => {
		menuSendingIntervalRef.current = window.setTimeout(async () => {
			try {
				const CancelToken = axios.CancelToken;
				const newCancelToken = CancelToken.source();
				cancelTokenSource.current = newCancelToken;

				const res = await api.organization().getExportMenuProgress(appState.app.id, jobId, {
					cancelToken: newCancelToken.token,
				});
				if (res.job_status === "NEW" || res.job_status === "STARTED") {
					setMenuSending(true);
					handleSendingMenuTimeout(res.job_uid);
				} else {
					setMenuSending(false);
					await fetchApplication();
					window.clearTimeout(menuSendingIntervalRef.current);
					setMenuErrors([]);
					addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
				}
			} catch (err) {
				const errs = handleException(err);
				if (errs[0].message === "canceled") return;
				handleError.form(err, setError, addFlash);
				setMenuErrors(handleException(err));
				setMenuSending(false);
				window.clearTimeout(menuSendingIntervalRef.current);
			}
		}, 1000);
	};

	const sendMenu = async () => {
		setMenuSending(true);
		const params: Record<string, any> = { include: "settings" };
		try {
			const CancelToken = axios.CancelToken;
			const newCancelToken = CancelToken.source();
			cancelTokenSource.current = newCancelToken;
			const res = await api.organization().exportMenu(appState.app.id, params, {
				cancelToken: newCancelToken.token,
			});
			handleSendingMenuTimeout(res);
		} catch (err) {
			const errs = handleException(err);
			if (errs[0].message === "canceled") return;
			handleError.form(err, setError, addFlash);
			setMenuSending(false);
		}
	};

	return (
		<>
			<div className="my-3">
				<h6>{t("modules.app.field.synchronization.title")}</h6>
				<Form.Group className="form-group">
					<FormCheck
						type={"switch"}
						register={register}
						label={t("modules.app.field.auto_update_menu_after_changes.title")}
						name="settings.auto_update_menu_after_changes"
						errors={errors}
					/>
					<ButtonLoading loading={menuSending} variant={"add"} onClick={() => sendMenu()}>
						{t("modules.app.action.send_menu.title")}
					</ButtonLoading>
				</Form.Group>
				<FormCheck
					type="switch"
					register={register}
					label={t("modules.app.field.send_menu_to_go_hub.title")}
					name="settings.send_menu_to_go_hub"
					errors={errors}
				/>
			</div>
			<hr />
		</>
	);
};

export default GoOrderSynchronizationForm;
